import {
  People,
  Approval,
  School,
  Groups,
  Lightbulb,
  Hub,
  Schedule,
  ManageAccounts,
  Assessment,
  Analytics,
  Science,
  MonitorHeart,
  PersonAdd,
  FormatListBulleted,
  AddBox,
  FolderOpen,
  VerifiedUser,
  AutoAwesome,
  Extension,
  Link,
  AddTask,
  ListAlt,
  Search,
  GridView,
  ErrorOutline,
  BarChart,
  History,
  Queue,
  Storage,
  Security,
  Memory,
  SmartToy,
  MenuBook,
  NewReleases,
  SupportAgent,
  ConfirmationNumber,
} from "@mui/icons-material";

type AdminAction = {
  titleKey: string;
  path?: string;
  externalUrl?: string;
  icon?: typeof PersonAdd;
};

type AdminRoute = {
  titleKey: string;
  icon: typeof People;
  descriptionKey?: string;
  layout?: "default" | "monitoring";
  actions: AdminAction[];
};

export const adminRoutes: AdminRoute[] = [
  {
    titleKey: "users",
    icon: People,
    descriptionKey: "manageSystemUsers",
    actions: [
      {
        titleKey: "createNew",
        path: "/users/create",
        icon: PersonAdd,
      },
      {
        titleKey: "viewAll",
        path: "/users",
        icon: FormatListBulleted,
      },
    ],
  },

  {
    titleKey: "workspace",
    icon: Hub,
    descriptionKey: "manageWorkspaces",
    actions: [
      {
        titleKey: "createNew",
        path: "/workspace/create",
        icon: AddBox,
      },
      {
        titleKey: "viewAll",
        path: "/workspace",
        icon: FolderOpen,
      },
    ],
  },

  {
    titleKey: "certificationCategories",
    icon: Approval,
    descriptionKey: "manageCertificationCategories",
    actions: [
      {
        titleKey: "createNew",
        path: "/cert_category/create",
        icon: AddBox,
      },
      {
        titleKey: "viewAll",
        path: "/cert_category",
        icon: VerifiedUser,
      },
    ],
  },

  {
    titleKey: "knowledgeBases",
    icon: School,
    descriptionKey: "manageKnowledgeRepositories",
    actions: [
      {
        titleKey: "createNew",
        path: "/knowledge_base/create",
        icon: AddBox,
      },
      {
        titleKey: "viewAll",
        path: "/knowledge_base",
        icon: FolderOpen,
      },
    ],
  },

  {
    titleKey: "alanCredentials",
    icon: ManageAccounts,
    descriptionKey: "manageAlanCredentials",
    actions: [
      {
        titleKey: "createNew",
        path: "/alan_credentials/create",
        icon: AddBox,
      },
      {
        titleKey: "viewAll",
        path: "/alan_credentials",
        icon: ManageAccounts,
      },
    ],
  },

  {
    titleKey: "gradingCriteria",
    icon: Assessment,
    descriptionKey: "manageGradingCriteria",
    actions: [
      {
        titleKey: "createNew",
        path: "/grading_criterion/create",
        icon: AddBox,
      },
      {
        titleKey: "viewAll",
        path: "/grading_criterion",
        icon: Assessment,
      },
    ],
  },

  {
    titleKey: "virtualTeamMembers",
    icon: Groups,
    descriptionKey: "manageAiTeamMembers",
    actions: [
      {
        titleKey: "createNew",
        path: "/como_agents/create",
        icon: PersonAdd,
      },
      {
        titleKey: "viewAll",
        path: "/como_agents",
        icon: Groups,
      },
    ],
  },

  {
    titleKey: "skills",
    icon: Lightbulb,
    descriptionKey: "manageAvailableSkills",
    actions: [
      {
        titleKey: "createNew",
        path: "/skill/create",
        icon: AutoAwesome,
      },
      {
        titleKey: "viewAll",
        path: "/skill",
        icon: Lightbulb,
      },
    ],
  },

  {
    titleKey: "mcpConnections",
    icon: Hub,
    descriptionKey: "manageMcpIntegrations",
    actions: [
      {
        titleKey: "createNew",
        path: "/mcp_connections/create",
        icon: Link,
      },
      {
        titleKey: "viewAll",
        path: "/mcp_connections",
        icon: Extension,
      },
    ],
  },

  {
    titleKey: "scheduledTasks",
    icon: Schedule,
    descriptionKey: "manageAutomatedTasks",
    actions: [
      {
        titleKey: "createNew",
        path: "/scheduled_tasks/create",
        icon: AddTask,
      },
      {
        titleKey: "viewAll",
        path: "/scheduled_tasks",
        icon: ListAlt,
      },
    ],
  },

  {
    titleKey: "helpDesk",
    icon: SupportAgent,
    descriptionKey: "manageHelpDeskTickets",
    actions: [
      {
        titleKey: "manageTickets",
        path: "/help-desk-admin",
        icon: ConfirmationNumber,
      },
    ],
  },

  {
    titleKey: "aiTesting",
    icon: Science,
    descriptionKey: "aiTestingUtilities",
    actions: [
      {
        titleKey: "retrievalTest",
        path: "/retrieval_test",
        icon: Search,
      },
      {
        titleKey: "gridSearchTest",
        path: "/grid_search_test",
        icon: GridView,
      },
    ],
  },

  {
    titleKey: "featureCatalog",
    icon: MenuBook,
    descriptionKey: "featureCatalogDescription",
    actions: [
      {
        titleKey: "viewAll",
        path: "/features_catalog",
        icon: NewReleases,
      },
    ],
  },

  {
    titleKey: "monitoring",
    icon: Analytics,
    layout: "monitoring",
    actions: [
      {
        titleKey: "errors",
        path: "/error_monitoring",
        icon: ErrorOutline,
      },
      {
        titleKey: "Sentry Monitoring",
        externalUrl:
          "https://corporate-momentum.sentry.io/issues/?environment=development&referrer=sidebar&statsPeriod=1h",
        icon: MonitorHeart,
      },
      {
        titleKey: "usage",
        path: "/usage",
        icon: BarChart,
      },
      {
        titleKey: "actionLogs",
        path: "/action_log",
        icon: History,
      },
      {
        titleKey: "queueMonitoring",
        path: "/queue_monitoring",
        icon: Queue,
      },
      {
        titleKey: "databaseMonitoring",
        path: "/db_monitoring",
        icon: Storage,
      },
      {
        titleKey: "authMonitoring",
        path: "/auth_monitoring",
        icon: Security,
      },
      {
        titleKey: "hardwareMonitoring",
        path: "/hardware_monitoring",
        icon: Memory,
      },
      {
        titleKey: "agentMonitoring",
        path: "/agent_monitoring",
        icon: SmartToy,
      },
    ],
  },
];
